import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  TextField,
  Grid,
  Link,
  CircularProgress,
  Paper,
  Typography,
} from '@material-ui/core';
import { PageableTable } from 'components/PageableTable';
import { PageableTableHeader } from 'components/PageableTable/components';
import { DateDisplay } from 'components/DateDisplay/DateDisplay';
import { fetchFolderLogs } from 'lib/Api/Drive';
import { DriveFolderLog, FetchFolderLogsResponse } from 'lib/Model/Drive';
import {
  getTableSettingsStorageKey,
  useLocalStorageSettings,
  useDebounce,
  TableSettings,
} from 'hooks';

const useStyles = makeStyles((theme) => ({
  searchBox: {
    padding: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    overflow: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  errorMessage: {
    color: theme.palette.error.main,
    margin: theme.spacing(2),
    textAlign: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  actionButton: {
    padding: theme.spacing(0.5),
  },
}));

interface DriveHistoryTableProps {
  storageKeySuffix?: string;
}

export const DriveHistoryTable: React.FC<DriveHistoryTableProps> = ({
  storageKeySuffix = 'global',
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [folderLogs, setFolderLogs] = useState<
    FetchFolderLogsResponse | undefined
  >();
  const [pageNumber, setPageNumber] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [textSearch, setTextSearch] = useState<string | undefined>();
  const debouncedTextSearch = useDebounce(textSearch, 400);

  const storageKey = getTableSettingsStorageKey(
    'driveHistory',
    storageKeySuffix,
  );
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(storageKey, { rowsPerPage: 10 });

  const fetchFolderLogsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchFolderLogs({
        searchParam: debouncedTextSearch,
        page: pageNumber + 1,
        pageSize: tableSettings.rowsPerPage,
      });
      setFolderLogs(data);
    } catch (err) {
      setError(
        `Failed to load folder logs: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedTextSearch, pageNumber, tableSettings.rowsPerPage]);

  useEffect(() => {
    fetchFolderLogsData();
  }, [fetchFolderLogsData]);

  useEffect(() => {
    setPageNumber(0);
  }, [debouncedTextSearch, tableSettings.rowsPerPage]);

  const historyColumns: PageableTableHeader[] = [
    {
      key: 'created_on',
      label: 'Created Date',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'folder_name',
      label: 'Folder Name',
      sortable: false,
      props: { align: 'left' },
    },
    {
      key: 'created_by',
      label: 'Created By',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'folder_url',
      label: 'Link to Folder',
      sortable: false,
      props: { align: 'center' },
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            placeholder="Search by folder name, company name or company registration number"
            value={textSearch ?? ''}
            className={classes.searchBox}
            onChange={(e) => setTextSearch(e.target.value || undefined)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={loading}
          />
        </Grid>
      </Grid>

      {loading && !folderLogs ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : error ? (
        <Typography variant="body1" className={classes.errorMessage}>
          {error}
        </Typography>
      ) : folderLogs?.data && folderLogs.data.length > 0 ? (
        <Paper className={classes.tableContainer}>
          <PageableTable
            tableSettings={tableSettings}
            setTableSettings={setTableSettings}
            showSkeletonLoading={loading}
            hideColumns={false}
            rows={
              folderLogs.data.map((log: DriveFolderLog, index: number) => {
                return {
                  key: index.toString(),
                  cells: [
                    {
                      key: 'created_on',
                      display: log.created_on && (
                        <DateDisplay date={log.created_on} />
                      ),
                      props: { align: 'center' },
                    },
                    {
                      key: 'folder_name',
                      display: `${log.folder_name} ${
                        log.company_name ? `(${log.company_name})` : ''
                      }`,
                      props: { align: 'left' },
                    },
                    {
                      key: 'created_by',
                      display: log.created_by,
                      props: { align: 'center' },
                    },
                    {
                      key: 'folder_url',
                      display: (
                        <Link
                          href={log.folder_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                        >
                          Open in Drive
                        </Link>
                      ),
                      props: { align: 'center' },
                    },
                  ],
                };
              }) || []
            }
            loading={loading}
            pageNumber={pageNumber}
            rowCount={folderLogs?.total_count ?? 0}
            columns={historyColumns}
            onChangePage={setPageNumber}
          />
        </Paper>
      ) : (
        <div className={classes.emptyState}>
          <Typography variant="body1">
            No folder creation logs found.
            {!textSearch ? (
              ''
            ) : (
              <>
                <br />
                <br />
                Try changing your search criteria.
              </>
            )}
          </Typography>
        </div>
      )}
    </>
  );
};
